/**
 * propertyMedia — data-access for the client reference-media collection
 * (`properties/{propertyId}/media`). READ-ONLY (Phase 2 staff pane). No write
 * functions live here — the write/ingest path (Mack) and the rules gate (Vex)
 * are owner-gated and out of scope for this module.
 *
 * Mirrors `propertyStore.ts`: Firestore modular SDK, live `onSnapshot`, returns
 * the Firestore `Unsubscribe`. The query SHAPE is built by the pure
 * `buildMediaQuerySpec` (propertyMediaModel.ts) so it stays unit-testable; this
 * file only maps that spec onto real `where(...)`/`orderBy(...)` constraints.
 *
 * Contract: scratchpad/media-read-contract.md §1–3 (FROZEN). The pane imports
 * the FROZEN type surface from here (re-exported below) + `listenPropertyMedia`.
 */
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  buildMediaQuerySpec,
  type MediaQuery,
  type PropertyMedia,
} from './propertyMediaModel';

// Re-export the FROZEN read surface so the pane can `import { PropertyMedia,
// listenPropertyMedia, MediaQuery, MediaScope } from './propertyMedia'`.
export type {
  MediaKind,
  MediaSource,
  MediaStatus,
  PropertyMedia,
  MediaScope,
  MediaQuery,
} from './propertyMediaModel';

/**
 * Live-subscribe to one property's client-media, walled to `clientId`.
 *
 * Live (not one-shot) by design: media arrives from four async channels while
 * staff evaluate, and this matches the codebase's collection-read pattern
 * (`listenPropertiesForClient` et al). Returns the Firestore `Unsubscribe` —
 * call it on unmount. `onError` surfaces a rules denial / missing index during
 * the security rollout without crashing the pane.
 */
export function listenPropertyMedia(
  q: MediaQuery,
  cb: (items: PropertyMedia[]) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  const spec = buildMediaQuerySpec(q);
  const col = collection(db, 'properties', q.propertyId, 'media');
  const constraints = [
    ...spec.where.map((c) => where(c.field, c.op, c.value)),
    orderBy(spec.orderBy.field, spec.orderBy.dir),
  ];
  return onSnapshot(
    query(col, ...constraints),
    (snap) => cb(snap.docs.map((d) => d.data() as PropertyMedia)),
    (err) => onError?.(err),
  );
}
